import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  Menu,
  MenuItem,
  CircularProgress,
  Alert,
  Snackbar,
  Avatar
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  MoreVert,
  FilterList,
  Visibility,
  Category as DefaultCategoryIcon,
  Refresh
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import categoryService from '../../services/categoryService';
import menuService from '../../services/menuService';

// Import dialog components
import ViewDetails from './dialogs/ViewDetails';
import EditCategory from './dialogs/EditCategory';
import DeleteDialog from './dialogs/DeleteDialog';
import SuccessDialog from './dialogs/SuccessDialog';

// Import all icons for dynamic rendering
import * as MuiIcons from '@mui/icons-material';

const CategoryList = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const MotionDiv = motion.div;
  
  // State variables
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState({ show: false, message: '' });
  const [hasUpdates, setHasUpdates] = useState(false); // Add this for real-time updates
  
  // Dialog states
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Fetch data on component mount
  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Socket listeners for real-time updates
  useEffect(() => {
    const handleCategoryUpdate = (event) => {
      const updateData = event.detail;
      console.log('📂 Category update received:', updateData);
      
      // Show update indicator instead of auto-refreshing
      setHasUpdates(true);
      
      // Optionally show a toast notification
      if (updateData.action === 'created') {
        console.log(`New category "${updateData.category?.name}" was added`);
      } else if (updateData.action === 'updated') {
        console.log(`Category "${updateData.category?.name}" was updated`);
      } else if (updateData.action === 'deleted') {
        console.log(`Category "${updateData.category?.name}" was deleted`);
      }
    };

    const handleMenuUpdate = (event) => {
      const updateData = event.detail;
      console.log('🍽️ Menu update received (affects category counts):', updateData);
      setHasUpdates(true);
    };

    // Add event listeners
    window.addEventListener('category-list-updated', handleCategoryUpdate);
    window.addEventListener('menu-list-updated', handleMenuUpdate);

    // Also try direct socket listeners as backup
    const socket = window.socket;
    if (socket) {
      socket.on('category-list-updated', handleCategoryUpdate);
      socket.on('menu-list-updated', handleMenuUpdate);
    }

    return () => {
      // Clean up event listeners
      window.removeEventListener('category-list-updated', handleCategoryUpdate);
      window.removeEventListener('menu-list-updated', handleMenuUpdate);
      
      // Clean up socket listeners
      if (socket) {
        socket.off('category-list-updated');
        socket.off('menu-list-updated');
      }
    };
  }, []);
  
  // Fetch all required data
  const fetchData = async () => {
    setLoading(true);
    try {
      const [categoriesData, menuItemsData] = await Promise.all([
        categoryService.getAll(),
        menuService.getAllWithCategories()
      ]);
      
      // Group menu items by category
      const menuItemsByCategory = menuItemsData.reduce((acc, item) => {
        if (!acc[item.category_id]) {
          acc[item.category_id] = [];
        }
        acc[item.category_id].push(item);
        return acc;
      }, {});
      
      // Add item count to categories
      const categoriesWithCounts = categoriesData.map(category => ({
        ...category,
        itemCount: (menuItemsByCategory[category.category_id] || []).length,
        items: menuItemsByCategory[category.category_id] || []
      }));
      
      setCategories(categoriesWithCounts);
      setFilteredCategories(categoriesWithCounts);
      setHasUpdates(false); // Clear update indicator after refresh
      setLoading(false);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(t('category.loadError'));
      setLoading(false);
    }
  };
  
  // Filter categories based on search term
  useEffect(() => {
    if (searchTerm) {
      const filtered = categories.filter(
        category => category.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCategories(filtered);
    } else {
      setFilteredCategories(categories);
    }
  }, [categories, searchTerm]);
  
  // Handle action menu open
  const handleMenuOpen = (event, category) => {
    setAnchorEl(event.currentTarget);
    setSelectedCategory(category);
  };
  
  // Handle action menu close
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  // Handle view category
  const handleViewDetails = () => {
    setViewDialogOpen(true);
    handleMenuClose();
  };
  
  // Handle edit category
  const handleEditCategory = () => {
    setEditDialogOpen(true);
    handleMenuClose();
  };
  
  // Handle delete confirmation dialog
  const handleDeleteConfirm = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };
  
  // Handle actual deletion
  const handleDeleteCategory = async (categoryId) => {
    try {
      await categoryService.delete(categoryId);
      
      // Update local state
      setCategories(categories.filter(cat => cat.category_id !== categoryId));
      
      // Show success message
      setSuccessMessage(t('category.deleteSuccess'));
      setSuccessDialogOpen(true);
      
      return true;
    } catch (err) {
      console.error('Error deleting category:', err);
      setError(t('category.updateError'));
      return false;
    }
  };
  
  // Handle search input
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };
  
  // Navigate to add category
  const handleAddCategory = () => {
    navigate('/dashboard/category/add');
  };

  // Handle manual refresh
  const handleRefresh = () => {
    fetchData();
  };
  
  // Get icon component by name
  const getIconByName = (iconName) => {
    if (!iconName) return <DefaultCategoryIcon />;
    
    // Map of friendly names to MUI icon component names
    const iconMap = {
      'Fastfood': 'Fastfood',
      'Drinks': 'LocalDrink',
      'Dessert': 'Cake',
      'Coffee': 'Coffee',
      'Menu': 'RestaurantMenu',
      'Bar': 'LocalBar',
      'Tea': 'EmojiFoodBeverage',
      'Ice Cream': 'Icecream',
      'Pizza': 'LocalPizza',
      'Dining': 'LocalDining',
      'Ramen': 'RamenDining',
      'Sushi': 'SetMeal',
      'Lunch': 'LunchDining',
      'Restaurant': 'Restaurant',
      'Wine': 'Liquor',
      'Soup': 'SoupKitchen',
      'Breakfast': 'BreakfastDining',
      'Bakery': 'BakeryDining',
      'Dinner': 'DinnerDining',
      'Egg': 'EggAlt',
      'Kebab': 'KebabDining',
      'Tapas': 'SportsBar'
    };
    
    // Get the component name from our map
    const componentName = iconMap[iconName];
    
    // Try to get the icon from MUI icons
    if (componentName && MuiIcons[componentName]) {
      const IconComponent = MuiIcons[componentName];
      return <IconComponent />;
    }
    
    // Fallback for icons that don't exist
    return <DefaultCategoryIcon />;
  };
  
  // Close error snackbar
  const handleCloseError = () => {
    setError(null);
  };
  
  // Close success snackbar
  const handleCloseSuccess = () => {
    setSuccess({ show: false, message: '' });
  };
  
  return (
    <Box>
      <MotionDiv
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4, alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
              {t('category.listTitle')}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t('category.listSubtitle')}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={handleRefresh}
              disabled={loading}
            >
              Refresh
            </Button>
            
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddCategory}
              sx={{ 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a71d6 0%, #6a4292 100%)'
                }
              }}
            >
              {t('category.addButton')}
            </Button>
          </Box>
        </Box>
      </MotionDiv>
      
      {/* Error Message */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Update Indicator */}
      {hasUpdates && (
        <Alert 
          severity="info" 
          sx={{ mb: 3 }}
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={handleRefresh}
              disabled={loading}
            >
              {loading ? 'Refreshing...' : 'Refresh Now'}
            </Button>
          }
        >
          🔄 Category list has been updated by another user. Click refresh to see changes.
        </Alert>
      )}
      
      <MotionDiv
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <Card sx={{ mb: 4 }}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <TextField
                placeholder={t('category.searchPlaceholder')}
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={handleSearchChange}
                sx={{ width: 300 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
              
              <Box>
                <Button
                  startIcon={<FilterList />}
                  variant="outlined"
                  size="small"
                  sx={{ ml: 1 }}
                >
                  {t('category.filter')}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </MotionDiv>
      
      <MotionDiv
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        {loading ? (
          <Box display="flex" justifyContent="center" my={5}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('category.table.number')}</TableCell>
                  <TableCell>{t('category.table.name')}</TableCell>
                  <TableCell>{t('category.table.icon')}</TableCell>
                  <TableCell>{t('category.table.description')}</TableCell>
                  <TableCell>{t('category.table.items')}</TableCell>
                  <TableCell align="right">{t('category.table.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCategories.map((category, index) => (
                  <TableRow 
                    key={category.category_id}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar 
                          sx={{ 
                            width: 32, 
                            height: 32,
                            bgcolor: category.colorValue || 'primary.main',
                            '& .MuiSvgIcon-root': { fontSize: 18 }
                          }}
                        >
                          {getIconByName(category.icon)}
                        </Avatar>
                        <Typography variant="body2">{category.name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {category.icon ? (
                        <Chip 
                          icon={getIconByName(category.icon)} 
                          label={category.icon}
                          size="small"
                          sx={{ 
                            bgcolor: 'background.default',
                            '& .MuiChip-icon': { fontSize: 16 }
                          }}
                        />
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>{category.description || '—'}</TableCell>
                    <TableCell>{category.itemCount || 0}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(event) => handleMenuOpen(event, category)}
                      >
                        <MoreVert fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                
                {filteredCategories.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('category.noCategories')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </MotionDiv>
      
      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: { 
            minWidth: 180,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            borderRadius: 2
          }
        }}
      >
        <MenuItem onClick={handleViewDetails}>
          <Visibility fontSize="small" sx={{ mr: 1.5 }} />
          {t('category.viewDetails')}
        </MenuItem>
        <MenuItem onClick={handleEditCategory}>
          <Edit fontSize="small" sx={{ mr: 1.5 }} />
          {t('common.edit')}
        </MenuItem>
        <MenuItem 
          onClick={handleDeleteConfirm}
          sx={{ color: 'error.main' }}
        >
          <Delete fontSize="small" sx={{ mr: 1.5 }} />
          {t('common.delete')}
        </MenuItem>
      </Menu>
      
      {/* View Details Dialog */}
      <ViewDetails
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        category={selectedCategory}
        onEdit={() => {
          setViewDialogOpen(false);
          setEditDialogOpen(true);
        }}
      />
      
      {/* Edit Dialog */}
      <EditCategory
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        category={selectedCategory}
        refreshData={fetchData}
      />
      
      {/* Delete Dialog */}
      <DeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        category={selectedCategory}
        onDelete={handleDeleteCategory}
      />
      
      {/* Success Dialog */}
      <SuccessDialog
        open={successDialogOpen}
        onClose={() => setSuccessDialogOpen(false)}
        message={successMessage}
      />
      
      {/* Error Snackbar */}
      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
      
      {/* Success Snackbar */}
      <Snackbar 
        open={success.show} 
        autoHideDuration={6000} 
        onClose={handleCloseSuccess}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSuccess} severity="success" sx={{ width: '100%' }}>
          {success.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CategoryList;